import { useNavigateWithQueryParams } from "@App/hooks/useNavigateWithQueryParams";
import type { Brand, Organization, PremiumService, Service } from "@App/types";
import {
  OrganizationHasSelectableBevSupport,
  ServiceShouldDisplayBevOnCurrentList,
} from "@App/utils/conditionals/combinations";
import { OrganizationIsALD, OrganizationIsAyvens } from "@App/utils/conditionals/organization";
import { ServiceIsGlass } from "@App/utils/conditionals/services";
import { getServiceNameLabel } from "@App/utils/getServiceNameLabel";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import { type FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

type FilterOverviewProps = {
  organization: Organization;
  service: Service;
  premiumServices?: PremiumService[] | null;
  brand?: Brand | null;
  bevSupport?: boolean;
  commercialVehicleSupport?: boolean;
};

export const FilterOverview: FunctionComponent<FilterOverviewProps> = ({
  organization,
  service,
  premiumServices = null,
  brand = null,
  bevSupport = false,
  commercialVehicleSupport = false,
}: FilterOverviewProps) => {
  const { t } = useTranslation();

  const serviceName = getServiceNameLabel(organization, service, premiumServices);
  const { navigateWithQuery } = useNavigateWithQueryParams();

  const handleBack = () => {
    navigateWithQuery("..", true);
  };

  return (
    <div className="relative bg-white hidden md:block border border-neutral-500 py-2 pl-8 pr-2 rounded">
      <span
        className={
          "absolute left-0 top-0 h-full w-4 pl-1 grid content-center border-r border-neutral-500"
        }
      >
        <ChevronLeftIcon className={"text-neutral-500 h-4 w-4 -ml-1"} />
      </span>
      <div className={"mb-2"}>
        <p className={"font-bold"}>{t("filters.service")}</p>
        <span>{serviceName}</span>
      </div>
      {brand && (
        <div className={"mb-2"}>
          <p className={"font-bold"}>{t("filters.brand")}</p>
          <span>{brand?.name}</span>
        </div>
      )}
      {((OrganizationHasSelectableBevSupport(organization) &&
        ServiceShouldDisplayBevOnCurrentList(service, organization)) ||
        OrganizationIsALD(organization)) && (
        <div className={"mb-2"}>
          <p className={"font-bold"}>{t("filters.engineType")}</p>
          <span>{bevSupport ? t("Elektro-/Hybridmotor") : t("Verbrennungsmotor")}</span>
        </div>
      )}
      {OrganizationIsAyvens(organization) && !ServiceIsGlass(service) && (
        <div>
          <p className={"font-bold"}>{t("filters.vehicleType")}</p>
          <span>{commercialVehicleSupport ? t("Nutzfahrzeug") : t("PKW")}</span>
        </div>
      )}
      <button
        type={"button"}
        onClick={handleBack}
        className={"absolute w-full h-full pointer bottom-0 left-0"}
      >
        <span className={"hidden"}>{t("back")}</span>
      </button>
    </div>
  );
};
